/**
 * AttachMenu
 *
 * Menu do clipe de anexo, no mesmo formato do WhatsApp: "Fotos e vídeos",
 * "Documento" e "Câmera" (só no celular).
 *
 * Componente único de propósito — os quatro inboxes (WhatsApp pessoal,
 * WhatsApp Business, Instagram e Messenger) usam este mesmo menu, então o
 * comportamento de anexo é idêntico entre eles. Instagram não recebe
 * documento, e informa isso pela prop `allowDocument`.
 *
 * A diferença entre os dois primeiros itens não é cosmética:
 * - "Fotos e vídeos" envia como mídia — o servidor comprime o que passar do
 *   limite do canal, como o próprio WhatsApp faz.
 * - "Documento" envia o arquivo cru, sem comprimir, usando o limite de
 *   documento (bem maior). É o caminho para mandar um vídeo pesado (.mov, por
 *   exemplo) preservando a qualidade original.
 */
import React, { useEffect, useRef } from 'react';
import { Image as ImageIcon, FileText, Camera } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const isMobile = () =>
  typeof navigator !== 'undefined' &&
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const AttachMenu = ({ open, onClose, onPick, allowDocument = true }) => {
  const { colors } = useTheme();
  const menuRef = useRef(null);

  // Fecha ao clicar fora ou apertar Esc, como qualquer menu do app.
  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose?.();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const items = [
    {
      key: 'media',
      label: 'Fotos e vídeos',
      hint: 'Comprime automaticamente se passar do limite',
      icon: ImageIcon,
      color: '#43A047',
    },
    allowDocument && {
      key: 'document',
      label: 'Documento',
      hint: 'Envia o arquivo sem comprimir, na qualidade original',
      icon: FileText,
      color: '#FB8C00',
    },
    isMobile() && {
      key: 'camera',
      label: 'Câmera',
      hint: null,
      icon: Camera,
      color: '#E53935',
    },
  ].filter(Boolean);

  const styles = {
    menu: {
      position: 'absolute',
      bottom: 'calc(100% + 8px)',
      left: 0,
      minWidth: 260,
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: 12,
      boxShadow: colors.shadowLg,
      padding: 6,
      zIndex: 50,
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      width: '100%',
      padding: '10px 12px',
      background: 'none',
      border: 'none',
      borderRadius: 8,
      cursor: 'pointer',
      textAlign: 'left',
      color: colors.textPrimary,
    },
    iconWrap: (color) => ({
      width: 34,
      height: 34,
      borderRadius: '50%',
      backgroundColor: `${color}1a`,
      color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }),
    label: { fontSize: 14, fontWeight: 500 },
    hint: { fontSize: 11, color: colors.textSecondary, marginTop: 2 },
  };

  return (
    <div ref={menuRef} style={styles.menu} role="menu">
      {items.map(({ key, label, hint, icon: Icon, color }) => (
        <button
          key={key}
          type="button"
          role="menuitem"
          style={styles.item}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.bgSecondary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onClick={() => {
            onClose?.();
            onPick?.(key);
          }}
        >
          <span style={styles.iconWrap(color)}>
            <Icon size={17} />
          </span>
          <span>
            <div style={styles.label}>{label}</div>
            {hint && <div style={styles.hint}>{hint}</div>}
          </span>
        </button>
      ))}
    </div>
  );
};

export default AttachMenu;
